import { PatientFollowUps } from '@/components/follow-ups/patient-follow-ups'
import { Header } from '@/components/layout/header'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import { WhatsAppButton } from '@/components/whatsapp'
import { Textarea } from '@/components/ui/textarea'
import {
  usePatient,
  usePatientInvoices,
  useSetPatientBlacklist,
  useUpdatePatient,
} from '@/hooks/usePatients'
import { useAuth } from '@/providers/auth-provider'
import { cn, formatCurrency, maskNRIC } from '@/lib/utils'
import { isValidWhatsAppNumber } from '@/lib/whatsapp'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import {
  ArrowLeft,
  Ban,
  Calendar,
  ChevronLeft,
  ChevronRight,
  FileText,
  Loader2,
  Mail,
  MapPin,
  MessageCircle,
  Pencil,
  Phone,
  Save,
  User,
  X,
} from 'lucide-react'
import * as React from 'react'

export const Route = createFileRoute('/patients_/$id')({
  component: PatientDetailPage,
})

function PatientDetailPage() {
  const navigate = useNavigate()
  const { id } = Route.useParams()
  const patientId = Number(id)

  const [mode, setMode] = React.useState<'view' | 'edit'>('view')
  const [activeTab, setActiveTab] = React.useState<'info' | 'invoices' | 'follow-ups'>('info')
  const [invoicePage, setInvoicePage] = React.useState(1)

  const { data, isPending } = usePatient(patientId)
  const { data: invoicesData, isPending: invoicesLoading } = usePatientInvoices(patientId, {
    page: invoicePage,
    pageSize: 10,
  })
  const updateMutation = useUpdatePatient()
  const blacklistMutation = useSetPatientBlacklist()
  const { hasRole } = useAuth()
  const canUnblacklist = hasRole(['super_admin', 'admin', 'branch_manager'])
  const [blacklistOpen, setBlacklistOpen] = React.useState(false)
  const [blacklistReason, setBlacklistReason] = React.useState('')

  const patient = data?.data
  const isBlacklisted = !!patient?.blacklistedAt

  const handleBlacklistConfirm = async () => {
    if (!patient) return
    if (isBlacklisted) {
      await blacklistMutation.mutateAsync({ id: patient.id, data: { blacklisted: false } })
    } else {
      const reason = blacklistReason.trim()
      if (!reason) return
      await blacklistMutation.mutateAsync({ id: patient.id, data: { blacklisted: true, reason } })
    }
    setBlacklistOpen(false)
    setBlacklistReason('')
  }
  const stats = data?.stats
  const invoices = invoicesData?.data ?? []
  const invoicesPagination = invoicesData
    ? { total: invoicesData.total, pageCount: invoicesData.pageCount }
    : null

  // Form state
  const [formData, setFormData] = React.useState({
    name: '',
    nric: '',
    phone: '',
    email: '',
    gender: '',
    address: '',
  })

  // Sync form data when patient data loads or mode changes to edit
  React.useEffect(() => {
    if (patient) {
      setFormData({
        name: patient.name || '',
        nric: patient.nric || '',
        phone: patient.phone || '',
        email: patient.email || '',
        gender: patient.gender || '',
        address: patient.address || '',
      })
    }
  }, [patient])

  const handleSave = async () => {
    if (!patientId) return

    await updateMutation.mutateAsync({
      id: patientId,
      data: {
        name: formData.name || undefined,
        nric: formData.nric || null,
        phone: formData.phone || null,
        email: formData.email || null,
        gender: (formData.gender as 'Male' | 'Female') || null,
        address: formData.address || null,
      },
    })

    setMode('view')
  }

  const subtitle = patient ? `PNO: ${patient.pno} · ${patient.branchName}` : 'Loading...'

  return (
    <div className="flex flex-col h-full">
      <Header
        title={isPending ? 'Patient Profile' : patient?.name || 'Patient Profile'}
        subtitle={subtitle}
      />

      <div className="flex-1 overflow-auto p-3 sm:p-4 lg:p-6">
        {/* Top bar: back + edit/save/cancel */}
        <div className="flex items-center justify-between mb-6">
          <Button
            variant="ghost"
            size="sm"
            className="-ml-2 text-text-muted hover:text-text-dark"
            onClick={() => navigate({ to: '/patients' })}
          >
            <ArrowLeft className="w-4 h-4 mr-1" />
            Back to Patients
          </Button>
          <div className="flex items-center gap-2">
            {mode === 'view' && patient && (
              <Button
                variant={isBlacklisted ? 'outline' : 'ghost'}
                size="sm"
                className={cn(
                  !isBlacklisted && 'text-destructive hover:bg-destructive/10 hover:text-destructive'
                )}
                disabled={isBlacklisted && !canUnblacklist}
                title={
                  isBlacklisted && !canUnblacklist
                    ? 'Only an admin can remove a patient from the blacklist'
                    : undefined
                }
                onClick={() => setBlacklistOpen(true)}
              >
                <Ban className="w-4 h-4 mr-2" />
                {isBlacklisted ? 'Remove from blacklist' : 'Blacklist'}
              </Button>
            )}
            {mode === 'view' ? (
              <Button variant="outline" size="sm" onClick={() => setMode('edit')}>
                <Pencil className="w-4 h-4 mr-2" />
                Edit
              </Button>
            ) : (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setMode('view')}
                  disabled={updateMutation.isPending}
                >
                  <X className="w-4 h-4 mr-1" />
                  Cancel
                </Button>
                <Button size="sm" onClick={handleSave} disabled={updateMutation.isPending}>
                  {updateMutation.isPending ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Save className="w-4 h-4 mr-2" />
                  )}
                  Save
                </Button>
              </>
            )}
          </div>
        </div>

        {isPending ? (
          <div className="space-y-6 max-w-3xl">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[1, 2, 3, 4].map((i) => (
                <Skeleton key={i} className="h-24" />
              ))}
            </div>
            <Skeleton className="h-32 w-full" />
            <Skeleton className="h-48 w-full" />
          </div>
        ) : patient ? (
          <div className="space-y-6 max-w-3xl">
            {isBlacklisted && (
              <div className="flex items-start gap-3 rounded-xl border border-destructive/30 bg-destructive/5 p-4">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-destructive/10">
                  <Ban className="h-5 w-5 text-destructive" />
                </div>
                <div className="text-sm">
                  <div className="font-semibold text-destructive">
                    Blacklisted — do not contact
                  </div>
                  <div className="mt-0.5 text-text-muted">
                    Excluded from all follow-up queues and WhatsApp campaigns since{' '}
                    {new Date(patient.blacklistedAt as string).toLocaleDateString('en-MY', {
                      day: '2-digit',
                      month: 'short',
                      year: 'numeric',
                      timeZone: 'Asia/Kuala_Lumpur',
                    })}
                    {patient.blacklistedByName ? ` by ${patient.blacklistedByName}` : ''}.
                  </div>
                  {patient.blacklistReason && (
                    <div className="mt-1.5 text-text-dark">
                      Reason: &ldquo;{patient.blacklistReason}&rdquo;
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Stats Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <StatCard label="Total Visits" value={String(stats?.totalInvoices ?? 0)} />
              <StatCard label="Total Spent" value={formatCurrency(stats?.totalRevenue ?? 0)} />
              <StatCard label="Avg / Visit" value={formatCurrency(stats?.avgPerVisit ?? 0)} />
              <StatCard
                label="Patient Since"
                value={
                  stats?.firstVisit
                    ? new Date(stats.firstVisit).toLocaleDateString('en-MY', {
                        month: 'short',
                        year: 'numeric',
                      })
                    : '-'
                }
              />
            </div>

            {/* WhatsApp Quick Action */}
            {!isBlacklisted && isValidWhatsAppNumber(patient.phone) && (
              <div className="rounded-xl border border-green-500/20 bg-green-500/5 p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-full bg-green-500/10">
                      <MessageCircle
                        className="h-5 w-5"
                        style={{ color: '#25D366' }}
                        fill="#25D366"
                      />
                    </div>
                    <div>
                      <div className="font-medium text-sm">Send WhatsApp</div>
                      <div className="text-xs text-muted-foreground">{patient.phone}</div>
                    </div>
                  </div>
                  <WhatsAppButton
                    phone={patient.phone}
                    patientName={patient.name}
                    branchName={patient.branchName}
                  />
                </div>
              </div>
            )}

            {/* Tabs */}
            <div className="flex gap-1 border-b border-border">
              <button
                type="button"
                className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === 'info'
                    ? 'border-primary text-primary'
                    : 'border-transparent text-muted-foreground hover:text-foreground'
                }`}
                onClick={() => setActiveTab('info')}
              >
                Personal Info
              </button>
              <button
                type="button"
                className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === 'invoices'
                    ? 'border-primary text-primary'
                    : 'border-transparent text-muted-foreground hover:text-foreground'
                }`}
                onClick={() => setActiveTab('invoices')}
              >
                Invoice History
              </button>
              <button
                type="button"
                className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === 'follow-ups'
                    ? 'border-primary text-primary'
                    : 'border-transparent text-muted-foreground hover:text-foreground'
                }`}
                onClick={() => setActiveTab('follow-ups')}
              >
                Follow-ups
              </button>
            </div>

            {/* Tab Content */}
            {activeTab === 'info' && (
              <div className="rounded-xl border border-border bg-card p-5">
                {mode === 'view' ? (
                  <div className="space-y-3">
                    <InfoRow icon={User} label="Name" value={patient.name} />
                    <InfoRow
                      icon={FileText}
                      label="NRIC"
                      value={patient.nric ? maskNRIC(patient.nric) : '-'}
                    />
                    <InfoRow icon={Phone} label="Phone" value={patient.phone || '-'} />
                    <InfoRow icon={Mail} label="Email" value={patient.email || '-'} />
                    <InfoRow icon={User} label="Gender" value={patient.gender || '-'} />
                    <InfoRow icon={MapPin} label="Address" value={patient.address || '-'} />
                    <InfoRow
                      icon={Calendar}
                      label="First Visit"
                      value={
                        stats?.firstVisit
                          ? new Date(stats.firstVisit).toLocaleDateString('en-MY')
                          : '-'
                      }
                    />
                    <InfoRow
                      icon={Calendar}
                      label="Last Visit"
                      value={
                        stats?.lastVisit
                          ? new Date(stats.lastVisit).toLocaleDateString('en-MY')
                          : '-'
                      }
                    />
                  </div>
                ) : (
                  <div className="space-y-4">
                    <FormField label="Name">
                      <Input
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      />
                    </FormField>
                    <FormField label="NRIC">
                      <Input
                        value={formData.nric}
                        onChange={(e) => setFormData({ ...formData, nric: e.target.value })}
                        placeholder="12 digits"
                      />
                    </FormField>
                    <FormField label="Phone">
                      <Input
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        placeholder="60xxxxxxxxx"
                      />
                    </FormField>
                    <FormField label="Email">
                      <Input
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      />
                    </FormField>
                    <FormField label="Gender">
                      <Select
                        value={formData.gender}
                        onValueChange={(value) => setFormData({ ...formData, gender: value })}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select gender" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Male">Male</SelectItem>
                          <SelectItem value="Female">Female</SelectItem>
                        </SelectContent>
                      </Select>
                    </FormField>
                    <FormField label="Address">
                      <Input
                        value={formData.address}
                        onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                      />
                    </FormField>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'invoices' && (
              <div className="rounded-xl border border-border bg-card">
                {invoicesLoading ? (
                  <div className="p-4 space-y-2">
                    {[1, 2, 3, 4, 5].map((i) => (
                      <Skeleton key={i} className="h-14 w-full" />
                    ))}
                  </div>
                ) : invoices.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-12 text-center">
                    <p className="text-sm text-muted-foreground">No invoices found</p>
                  </div>
                ) : (
                  <div className="divide-y divide-border">
                    {invoices.map((invoice) => (
                      <button
                        type="button"
                        key={invoice.id}
                        className="w-full flex items-center justify-between p-4 hover:bg-muted/50 transition-colors text-left"
                        onClick={() =>
                          navigate({
                            to: '/invoices/$id',
                            params: { id: String(invoice.id) },
                          })
                        }
                      >
                        <div>
                          <div className="font-medium text-sm">{invoice.invoiceNo}</div>
                          <div className="text-xs text-muted-foreground">
                            {new Date(invoice.invoiceDate).toLocaleDateString('en-MY', {
                              day: '2-digit',
                              month: 'short',
                              year: 'numeric',
                            })}
                            {invoice.paymentMethod && ` · ${invoice.paymentMethod}`}
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="font-medium">
                            {formatCurrency(Number.parseFloat(invoice.grandTotal))}
                          </div>
                          <div className="flex gap-1 justify-end mt-1">
                            {invoice.status && (
                              <Badge
                                variant="secondary"
                                className={`text-xs ${
                                  invoice.status === 'Paid'
                                    ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                                    : invoice.status === 'Void'
                                      ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                                      : ''
                                }`}
                              >
                                {invoice.status}
                              </Badge>
                            )}
                            {invoice.patientType && (
                              <Badge
                                variant={invoice.patientType === 'New' ? 'info' : 'secondary'}
                                className="text-xs"
                              >
                                {invoice.patientType}
                              </Badge>
                            )}
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                )}

                {/* Pagination */}
                {invoicesPagination && invoicesPagination.pageCount > 1 && (
                  <div className="flex items-center justify-between p-4 border-t border-border">
                    <p className="text-xs text-muted-foreground">
                      Page {invoicePage} / {invoicesPagination.pageCount} (
                      {invoicesPagination.total} invoices)
                    </p>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setInvoicePage((p) => Math.max(1, p - 1))}
                        disabled={invoicePage === 1}
                        className="h-7 px-2"
                      >
                        <ChevronLeft className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() =>
                          setInvoicePage((p) => Math.min(invoicesPagination.pageCount, p + 1))
                        }
                        disabled={invoicePage === invoicesPagination.pageCount}
                        className="h-7 px-2"
                      >
                        <ChevronRight className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'follow-ups' && <PatientFollowUps patientId={patientId} />}
          </div>
        ) : (
          <div className="flex items-center justify-center h-64 text-muted-foreground">
            Patient not found
          </div>
        )}
      </div>

      <AlertDialog open={blacklistOpen} onOpenChange={setBlacklistOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {isBlacklisted ? 'Remove from blacklist?' : `Blacklist ${patient?.name ?? 'patient'}?`}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {isBlacklisted
                ? 'The patient becomes eligible for follow-up queues and WhatsApp campaigns again from the next nightly run.'
                : 'The patient will be removed from every follow-up queue and all WhatsApp campaign audiences. Only an admin can undo this.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          {!isBlacklisted && (
            <div>
              <label
                htmlFor="profile-blacklist-reason"
                className="mb-1 block text-[11px] font-bold uppercase tracking-wide text-text-muted"
              >
                Reason (required)
              </label>
              <Textarea
                id="profile-blacklist-reason"
                value={blacklistReason}
                onChange={(e) => setBlacklistReason(e.target.value)}
                placeholder="e.g. Patient asked not to be contacted again"
                rows={3}
                className="text-sm"
                autoFocus
              />
            </div>
          )}
          <AlertDialogFooter>
            <AlertDialogCancel disabled={blacklistMutation.isPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className={cn(!isBlacklisted && 'bg-destructive text-white hover:bg-destructive/90')}
              disabled={blacklistMutation.isPending || (!isBlacklisted && !blacklistReason.trim())}
              onClick={(e) => {
                e.preventDefault()
                void handleBlacklistConfirm()
              }}
            >
              {blacklistMutation.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {isBlacklisted ? 'Remove from blacklist' : 'Blacklist patient'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}

function StatCard({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-xl border border-border bg-card p-4">
      <div className="text-2xl font-bold">{value}</div>
      <div className="text-xs text-muted-foreground">{label}</div>
    </div>
  )
}

function InfoRow({
  icon: Icon,
  label,
  value,
}: {
  icon: React.ComponentType<{ className?: string }>
  label: string
  value: string
}) {
  return (
    <div className="flex items-start gap-3">
      <Icon className="h-4 w-4 mt-0.5 text-muted-foreground" />
      <div className="flex-1">
        <div className="text-xs text-muted-foreground">{label}</div>
        <div className="text-sm">{value}</div>
      </div>
    </div>
  )
}

function FormField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-2">
      <label className="text-sm font-medium">{label}</label>
      {children}
    </div>
  )
}
